import { useEffect, useRef, useState } from 'react';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import './point-of-sale-container.styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingBasket } from '@fortawesome/free-solid-svg-icons';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import SearchBar from '@/components/SHARED/search-bar/search-bar.component';
import { getQueryFromFirestore } from '@/utils/getDataFromDbSimplified';
import PlayerCardPos from './player-card-pos.component';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { completeGameElementData } from '@/components/PREVIEW/previewGameElements';
import { UseCloudGameTransactions } from '@/utils/firebase.utils';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import useAnalytics from '@/utils/useAnalytics';
import { useGameTerms } from '@/utils/gameTerms';

const PointOfSaleContainer = ({
  gameData,
  playerList,
  teamMembers,
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const terms = useGameTerms();
  const [cart, setCart] = useState([]);
  const [recentlyAdded, setRecentlyAdded] = useState(new Set());
  const [elementsToView, setElementsToView] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const elementsRef = useRef();
  const approveRef = useRef(() => { });
  const elementsListenerRef = useRef(); 
  const { trackEvent } = useAnalytics();

  const gameId = gameData?.gameId;
  const isPreview = location.pathname.includes('/preview/manager/');

  useEffect(() => {
    if (!gameData?.gameName) return;
    changeDocumentTitle(`Point of Sale | Manage ${gameData?.gameName}`);
  }, [gameData?.gameName])

  useEffect(() => {
    if (!gameId) return;

    getElementsForSale();
    function getElementsForSale() {
      if (isPreview) {
        const elements = Object.values(completeGameElementData)
          .filter(e => {
            if (e.type === 'secretCode') return false;
            return e.opt_market && e.opt_available && e.status === 'active';
          }
          );
        const elementObj = {};
        elements.forEach(e => elementObj[e.id] = e);
        elementsRef.current = elementObj;
        setElementsToView(elements);
        return;
      }
      getQueryFromFirestore({
        'path': `games/${gameData.gameId}/gameElements`,
        'queryArray': [
          { 'field': 'opt_market', 'operator': '==', 'value': true },
          { 'field': 'opt_available', 'operator': '==', 'value': true },
          { 'field': 'status', 'operator': '==', 'value': 'active' },
        ],
        'callback': callback,
        'handleListener': handleListener
      })
      function callback(element) {
        if (element.opt_itemType === 'contribution') return; // Contributions not for sale in POS
        elementsRef.current = {
          ...elementsRef.current,
          ...{ [element.id]: element }
        }
        setElementsToView(Object.values(elementsRef.current));
      }
      function handleListener(listener) {
        elementsListenerRef.current = listener;
      }
    }

    return () => {
      if (elementsListenerRef.current) {
        elementsListenerRef.current();
      }
    }

  }, [gameId, isPreview, completeGameElementData]);

  const addToCart = (item) => {
    if (!selectedPlayer) {
      setAlertMessage('Please select a player before adding items to the cart.');
      return;
    }
    // Check if player has enough currency for this item + current cart total
    if (
      selectedPlayer.currency < item.opt_marketPrice ||
      (cart.reduce((sum, i) => sum + (i.opt_marketPrice * i.qty), 0) + item.opt_marketPrice) > selectedPlayer.currency
    ) {
      setAlertMessage(`Player does not have enough ${terms.currency.pluralLower} to purchase this item.`);
      return;
    }
    setCart(prevCart => {
      const existing = prevCart.find(i => i.id === item.id);
      if (existing) {
        return prevCart.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prevCart, { ...item, qty: 1 }];
    });
    setRecentlyAdded(prev => new Set([...prev, item.id]));
    setTimeout(() => {
      setRecentlyAdded(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }, 900);
  };

  const removeFromCart = (id) => {
    setCart(prevCart => prevCart.filter(item => item.id !== id));
  };

  function search(query) {
    const elementList = { ...elementsRef.current }
    if (!query) {
      setElementsToView(Object.values(elementList));
    } else {
      const lowerQuery = query.toLowerCase();
      setElementsToView(Object.values(elementList).filter(item => item.name.toLowerCase().includes(lowerQuery)));
    }
  }

  const cartTotal = cart.reduce((sum, item) => sum + (item.opt_marketPrice * item.qty), 0);

  function getSortKey(name) {
    return name.replace(/^the\s+/i, '').toLowerCase();
  }

  function getGroupedItems(items) {
    const groups = {};
    for (const item of items) {
      const cat = item.opt_cat || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    const sorted = Object.entries(groups).sort(([a], [b]) => getSortKey(a).localeCompare(getSortKey(b)));
    for (const [, catItems] of sorted) {
      catItems.sort((a, b) => getSortKey(a.name).localeCompare(getSortKey(b.name)));
    }
    return sorted;
  }

  function processPurchase() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }

    setConfirmMessage('Are you sure you want to complete this purchase?');
    approveRef.current = () => {
      continuePurchase();
    };

    async function continuePurchase() {
      const tempCart = [...cart];
      setLoading(true);
      const cartObj = {};
      for (const item of tempCart) {
        cartObj[item.id] = {
          'quantity': item.qty,
          'id': item.id,
          'cost': item.opt_marketPrice,
        }
      }
      const res = await UseCloudGameTransactions(
        'processShoppingCartPurchase',
        {
          'gameId': gameData.gameId,
          'playerId': selectedPlayer.playerId,
          'cart': cartObj,
          'sellerId': userProfile.appId,
        }
      )
      if (res.error) {
        setAlertMessage(`<p>Sorry, but your purchase couldn't go through. Here was the error message:</p><p>"${res.error}"</p>`);
        setLoading(false);
        trackException(`Shopping Cart Purchase Error: ${res.error}`);
        return;
      }
      console.log(res);
      setSelectedPlayer(prev => ({
        ...prev,
        currency: prev.currency - cartTotal
      }));
      setAlertMessage('Purchase completed successfully!<br /><br />If you purchased a prize pack, it may take a minute or so to populate in your Rewards page.');
      setLoading(false);
      trackEvent(
        'point_of_sale_purchase_completed',
        'Manager',
        `Manager ${userProfile.appId} completed a point of sale purchase to ${selectedPlayer.appId} in game ${gameData.gameId}`,
        tempCart.reduce((sum, item) => sum + (item.opt_marketPrice * item.qty), 0),
        null
      );
      setCart([]);
    }
  }

  return (
    <div className={`pos-container g-card ${screenSize}`} >
      <BackButton cancel={() => navigate(-1)} />
      <div className="g-space-1"></div>
      <div className="card-title">
        <FontAwesomeIcon icon={faShoppingBasket} />
        <span>Point of Sale</span>
      </div>

      <hr />


      <div className="pos-grid">

        {/* LEFT: ITEM GRID */}
        <div className="items-section">
          <SearchBar search={search} idLabel={"search-items"} />
          {getGroupedItems(elementsToView).map(([category, items]) => (
            <div key={category} className="category-group">
              <div className="category-header g-list-item">{category}</div>
              <div className="item-grid">
                {items.map(item => (
                  <div
                    key={item.id}
                    className={`item-card g-card clickable with-shadow${recentlyAdded.has(item.id) ? ' item-added' : ''}`}
                    onClick={() => addToCart(item)} >
                    {recentlyAdded.has(item.id) && (
                      <div className="add-overlay">
                        <span>Added</span>
                      </div>
                    )}
                    <ImageContainer
                      src={item.icon}
                      alt={item.name}
                      className="item-icon"
                      onError={(e) => e.target.src = 'https://via.placeholder.com/150?text=Item'}
                    />
                    <div className="element-info">
                      <div className="element-name">
                        {
                          item.name.length > 40
                            ? item.name.slice(0, 37) + '...'
                            : item.name
                        }
                      </div>
                      <div className="meta element-cost">
                        <ImageContainer src="/images/icons/coin.png" className="icon-small" />
                        {item.opt_marketPrice}
                      </div>
                    </div>
                    <div></div>
                    <button className="g-button primary small-btn static add-button" style={{ width: screenSize === 'mobile' ? 'auto' : '100%' }}>
                      Add to Cart
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT: SHOPPING CART */}
        <div className="cart-section">
          <PlayerCardPos
            gameData={gameData}
            playerList={playerList}
            teamMembers={teamMembers}
            selectedPlayer={selectedPlayer}
            setSelectedPlayer={setSelectedPlayer} />
          <div className="g-list-item cart-card">
            <div className='cart-header'>
              <span>Shopping Cart</span>
              <span className="badge" >
                {cart.reduce((sum, item) => sum + item.qty, 0)} items
              </span>
            </div>
            <hr className="small-margin" />

            {cart.length === 0 ? (
              <div className='empty-message'>Cart is empty</div>
            ) : (
              <div className="cart-items">
                {cart.map(item => (
                  <div key={item.id} className="cart-item g-list-item grey-background">
                    <div className="element-name">
                      <ImageContainer src={item.icon} alt={item.name} className="icon-med" />
                      {item.name}
                      <div className='qty g-list-item'>
                        x{item.qty}
                      </div>
                    </div>
                    <div className="g-space-0-5"></div>
                    <div className="cost-remove">
                      <div className="meta element-cost">
                        <span>Qty: <strong>{item.qty}</strong></span>
                        <div></div>
                        <span>Total:</span>
                        <ImageContainer src="/images/icons/coin.png" className="icon-small" />
                        {item.opt_marketPrice * item.qty}
                      </div>
                      <button
                        className="g-button text-only red tiny-btn"
                        onClick={() => removeFromCart(item.id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <hr />

            <div className="cart-total" >
              <div className="">Total:</div>
              <div className="total-cost">
                <ImageContainer src="/images/icons/coin.png" className="icon-med" />
                {cartTotal}
              </div>
            </div>
            <div className="g-space-1" />

            <button
              className={`g-button primary big-btn ${(
                cart.length === 0 ||
                !selectedPlayer
              ) ? 'disabled' : ''
                }`}
              style={{ width: '100%' }}
              onClick={() => processPurchase()}
            >
              Complete Purchase
            </button>

            <button
              className="g-button text-only"
              style={{ width: '100%', marginTop: '10px' }}
              onClick={() => setCart([])}
            >
              Clear Cart
            </button>
          </div>
        </div>
      </div>
      <FullPageLoader show={loading} />
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
      <ModalConfirm
        show={!!confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
    </div>
  );
}

export default PointOfSaleContainer